import { delay } from '@/utils/utils'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useGlobals } from '../composables/useGlobals'
import { type AsyncState, AsyncStateValue } from '../types'

export const useEntriesStore = defineStore('userEntries', () => {
  const entries = ref<any>([])
  const error = ref<string | null>(null)
  const state = ref<AsyncState>(AsyncStateValue.Pending)

  const { gameEntryService, gameSessionService, authenticationService } =
    useGlobals()

  const refresh = async () => {
    state.value = AsyncStateValue.Pending
    try {
      // If we previously hit an error, add a delay to allow the provider time
      // to recover before resending the request.
      if (error.value) {
        error.value = null
        await delay(2000)
      }
      const result = await gameEntryService.getEntries()
      entries.value = result.items
      state.value = AsyncStateValue.Resolved
    } catch (e) {
      error.value = 'Unable to load your entry history'
      state.value = AsyncStateValue.Rejected
      console.error(e)
    }
  }

  // Whenever a game session is completed, refresh the prize list
  gameSessionService.onSessionComplete(() => {
    if (authenticationService.isAuthenticated) {
      void refresh()
    }
  })

  // Watch for auth change so we can fetch the users entries when they log in,
  // or clear up if they log out.
  authenticationService.onStateChange((state, previousState) => {
    // Only perform a refresh if we've transitioned to `authenticated` from the
    // `authenticating` state (rather than `reauthenticating`, which happens
    // during a background token refresh)
    if (state === 'authenticated' && previousState === 'authenticating') {
      void refresh()
    } else if (state === 'unauthenticated') {
      entries.value.splice(0)
    }
  })

  // If the user is authenticated when the store is first instantiated, fetch
  // their entry data.
  if (authenticationService.isAuthenticated) {
    void refresh()
  }

  return {
    state,
    entries,
    refresh,
    error,
  }
})
